import React, { useState, useEffect, useRef } from "react";
import {
  Flex,
  Text,
  VStack,
  IconButton,
  Spinner,
} from "@chakra-ui/react";

import useIcons from "../../../../hooks/useIcons";
import { useStyles } from "../../../../hooks/useStyles";
import Cad from "../../../CustomUi/Cad";
import CustomAlert from "../../../CustomUi/CustomAlert";
import Pagination from "../../../CustomUi/Pagination";

// Components
import AuditTrailFilters from "./AuditTrailFilters";
import AuditTrailCard from "./AuditTrailCard";
import AuditTrailTable from "./AuditTrailTable";
import AuditTrailDetailModal from "./AuditTrailDetailModal";

// Hooks
import { useAuditTrailService } from "../../../../hooks/Domain/useAuditTrailService";

import { AuditTrailEntry } from "../../../../services/Dtos";
import {
  auditTrailActions,
  auditTrailEntityTypes,
} from "../../../../services/constants";
import {
  formatDateTime,
  getChangedProperties,
  getUserAgentInfo,
} from "../../../../services/Utils";
import { useBreakPoint } from "../../../../hooks/useBreakPoint";

const AuditTrailSettings: React.FC = () => {
  const { isMobile } = useBreakPoint();
  const icons = useIcons();
  const { scrollBar } = useStyles();

  // Service hook
  const {
    auditTrails,
    loading,
    errors,
    currentPage,
    totalPages,
    totalRecords,
    fetchAuditTrails,
    setCurrentPage,
    getUserInfo,
    userOptions,
    getActionColor,
  } = useAuditTrailService();

  // Filter states
  const [userIdFilter, setUserIdFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [entityTypeFilter, setEntityTypeFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTimeout, setSearchTimeout] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  const hasFetchedRef = useRef(false);

  // Modal state
  const [selectedEntry, setSelectedEntry] = useState<AuditTrailEntry | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchAuditTrails();
    }
  }, []);

  const handleFilter = () => {
    setCurrentPage(1);
    fetchAuditTrails({
      userId: userIdFilter,
      action: actionFilter,
      entityName: entityTypeFilter,
      startDate,
      endDate,
      search: searchTerm,
    });
  };

  const handleClearFilters = () => {
    setUserIdFilter("");
    setActionFilter("");
    setEntityTypeFilter("");
    setStartDate("");
    setEndDate("");
    setSearchTerm("");
    setCurrentPage(1);
    fetchAuditTrails();
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    const timeout = setTimeout(() => {
      handleFilter();
    }, 500);
    setSearchTimeout(timeout);
  };

  const handleRowClick = (entry: AuditTrailEntry) => {
    setSelectedEntry(entry);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedEntry(null);
  };

  const handlePreviousPage = () => {
    const newPage = Math.max(1, currentPage - 1);
    setCurrentPage(newPage);
    fetchAuditTrails(
      {
        userId: userIdFilter,
        action: actionFilter,
        entityName: entityTypeFilter,
        startDate,
        endDate,
        search: searchTerm,
      },
      undefined,
      newPage
    );
  };

  const handleNextPage = () => {
    const newPage = Math.min(totalPages, currentPage + 1);
    setCurrentPage(newPage);
    fetchAuditTrails(
      {
        userId: userIdFilter,
        action: actionFilter,
        entityName: entityTypeFilter,
        startDate,
        endDate,
        search: searchTerm,
      },
      undefined,
      newPage
    );
  };

  // Filter options
  const actionOptions = auditTrailActions;

  const entityTypeOptions = auditTrailEntityTypes;

  // Only apply client-side search filtering since server handles other filters
  const filteredAuditTrails = auditTrails.filter((entry) => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        entry.entityName.toLowerCase().includes(searchLower) ||
        entry.changes.toLowerCase().includes(searchLower) ||
        entry.entityId.toLowerCase().includes(searchLower) ||
        getUserInfo(entry.applicationUserId)
          .name.toLowerCase()
          .includes(searchLower) ||
        getUserInfo(entry.applicationUserId)
          .email.toLowerCase()
          .includes(searchLower)
      );
    }
    return true;
  });

  return (
    <VStack gap={6}>
      <Cad h={"auto"}>
        <AuditTrailFilters
          userIdFilter={userIdFilter}
          setUserIdFilter={setUserIdFilter}
          actionFilter={actionFilter}
          setActionFilter={setActionFilter}
          entityTypeFilter={entityTypeFilter}
          setEntityTypeFilter={setEntityTypeFilter}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          searchTerm={searchTerm}
          setSearchTerm={handleSearchChange}
          onFilter={handleFilter}
          onClearFilters={handleClearFilters}
          loading={loading}
          actionOptions={actionOptions}
          entityTypeOptions={entityTypeOptions}
          userOptions={userOptions}
        />
      </Cad>

      <Cad overflow={"hidden"}>
        <VStack gap={4} align="stretch" h={"full"}>
          <Flex
            direction={"row"}
            justify="space-between"
            align={"center"}
            gap={3}
            p={4}
          >
            <VStack gap={0} align="start">
              <Text fontSize="lg" fontWeight="bold">
                Audit Trail Entries
              </Text>
              <Text fontSize="sm" color="gray.500">
                {filteredAuditTrails.length} entries on this page
                {totalRecords > 0 && ` of ${totalRecords} total`}
              </Text>
            </VStack>
            <IconButton
              aria-label="Refresh"
              size="sm"
              onClick={() => fetchAuditTrails()}
              loading={loading}
              variant="ghost"
            >
              <icons.utility.refresh />
            </IconButton>
          </Flex>

          {errors.length > 0 && (
            <CustomAlert message={errors[0]} status="error" />
          )}

          {loading ? (
            <Flex justify="center" py={8}>
              <Spinner size="lg" />
            </Flex>
          ) : filteredAuditTrails.length === 0 ? (
            <CustomAlert
              message="No audit trail entries match your current filters."
              status="info"
            />
          ) : (
            <>
              {/* Mobile Card View */}
              {isMobile ? (
                <VStack
                  gap={3}
                  align="stretch"
                  h={"full"}
                  overflow={"auto"}
                  css={scrollBar}
                >
                  {filteredAuditTrails.map((entry) => (
                    <AuditTrailCard
                      key={entry.id}
                      entry={entry}
                      onRowClick={handleRowClick}
                      getUserInfo={getUserInfo}
                      getUserAgentInfo={getUserAgentInfo}
                      getActionColor={getActionColor}
                      formatDate={formatDateTime}
                    />
                  ))}
                </VStack>
              ) : (
                /* Desktop Table View */
                <AuditTrailTable
                  auditTrails={filteredAuditTrails}
                  onRowClick={handleRowClick}
                  getUserInfo={getUserInfo}
                  getUserAgentInfo={getUserAgentInfo}
                  getActionColor={getActionColor}
                  formatDate={formatDateTime}
                />
              )}

              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalRecords={totalRecords}
                currentPageRecords={filteredAuditTrails.length}
                onPreviousPage={handlePreviousPage}
                onNextPage={handleNextPage}
                loading={loading}
              />
            </>
          )}
        </VStack>
      </Cad>

      {/* Detail Modal */}
      <AuditTrailDetailModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        selectedEntry={selectedEntry}
        getUserInfo={getUserInfo}
        getActionColor={getActionColor}
        formatDate={formatDateTime}
        getChangedProperties={getChangedProperties}
      />
    </VStack>
  );
};

export default AuditTrailSettings;
